/**
 * Polling de /doctor/availability — chamada inicial + refresh a cada 60s.
 *
 * Substitui o useEffect inline que existia em ConsultaChat.jsx e Pagamento.jsx
 * (refactor 9f2b7d9 — 02/06/2026). Retorna um valor de DOCTOR_STATUS.
 *
 * Em 16/08/2026 o endpoint passou a devolver também o tamanho da fila, o tempo
 * médio informativo dos Termos e o braço do A/B que decide se essa expectativa
 * aparece antes do pagamento. `useDoctorAvailabilityDetails` expõe tudo;
 * `useDoctorAvailabilityPolling` segue devolvendo só o status, para não mexer
 * em quem só precisa dele.
 */

import { useEffect, useState } from 'react';
import { DOCTOR_STATUS, statusFromAvailability } from '../utils/doctorAvailability';

const REFRESH_INTERVAL_MS = 60_000;

// `Number(null)` é 0, então converter direto transformaria "não sei a fila" em
// "fila vazia" — a contagem pode voltar null (braço `hold` ou falha da agregação).
const finiteOrNull = (raw) => {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const INITIAL_DETAILS = {
  status: DOCTOR_STATUS.LOADING,
  queueTotal: null,
  typicalWaitMinutes: null,
  waitExpectationArm: null,
};

export function useDoctorAvailabilityDetails(baseUrl, userId = null) {
  const [details, setDetails] = useState(INITIAL_DETAILS);

  useEffect(() => {
    if (!baseUrl) return undefined;

    let cancelled = false;

    const fetchAvailability = async () => {
      try {
        const qs = userId ? `?userId=${encodeURIComponent(String(userId))}` : '';
        const res = await fetch(`${baseUrl}/doctor/availability${qs}`);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          setDetails({
            status: statusFromAvailability(data?.available),
            queueTotal: finiteOrNull(data?.queueTotal),
            typicalWaitMinutes: finiteOrNull(data?.typicalWaitMinutes),
            waitExpectationArm: data?.waitExpectationArm || null,
          });
        } else {
          setDetails((prev) => ({ ...prev, status: DOCTOR_STATUS.UNKNOWN }));
        }
      } catch (_err) {
        if (!cancelled) setDetails((prev) => ({ ...prev, status: DOCTOR_STATUS.UNKNOWN }));
      }
    };

    fetchAvailability();
    const interval = setInterval(fetchAvailability, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [baseUrl, userId]);

  return details;
}

export function useDoctorAvailabilityPolling(baseUrl, userId = null) {
  return useDoctorAvailabilityDetails(baseUrl, userId).status;
}

export default useDoctorAvailabilityPolling;
